import { STATUS_GUID } from '../../shared/utils/constants';
import { getStatusIdByGuidHelper } from '../../shared/utils/helper';

/**
 * Side-view query building and list filtering, with no state, service or component dependency.
 *
 * Split out of ticketViewFilters.ts, which reaches FeatureCache -> ConsentConfig -> App and so
 * cannot be loaded under jest at all. Keeping these two here is what lets them be tested.
 */

const SIDE_VIEW_SELECTED_EXPAND =
  '$expand=Requester,Resolution,TicketSlas($orderby=CreatedDate desc;$select=CreatedDate,ModifiedDate;$expand=SlaState,ServiceLevelAgreement($select=Name,IsActive,CreatedDate;$expand=SlaCriterias($select=Id,TimeDigitPart,TimeUnitPart,SlaActionTypeId,CreatedDate)))';

/**
 * Whether a custom view - rather than the legacy 0-4 dropdown - is deciding what the side list
 * contains, and therefore whether activeDropdownIndex can be read as an index at all.
 *
 * isCustomViewEnabled alone is not enough. It is React state seeded false and filled in after an
 * async licence check, so on the Tickets-list -> ticket path it can still be false while the
 * statuses are already warm from the list page. That is a window where neither guard in
 * filterSideViewTicketsForUpdatedTicket fires and the list is wiped - measured on bug 35324.
 *
 * selectedCustomViewId is the same key buildSideViewListQuery reads to build the query, so when
 * it is set the rows on screen came from a custom view whatever the licence state has got to.
 * Passed in rather than read here so this stays pure and testable under jest's node environment.
 */
export const isCustomViewDriven = (
  isCustomViewEnabled: boolean,
  selectedCustomViewId: string | null
): boolean => isCustomViewEnabled || Boolean(selectedCustomViewId);

/**
 * The side list's request. A view's custom property predicate goes in its own `cpfilter` parameter, as the ticket
 * list sends it: joined onto `$filter` it is not OData, so a view that filters only on a custom property (Location
 * is San Diego) was refused with a 400 and the list came back empty.
 */
export const sideViewListUrl = (filter: string, customFilter: string, rest: string): string =>
  '?' + [filter ? `$filter=${filter}` : '', rest, customFilter ? `cpfilter=${customFilter}` : ''].filter(Boolean).join('&');

export const buildSideViewSelectedTicketQuery = (filter: string, ticketId: number | string): string => {
  const selectedFilter = filter ? `(${filter}) and Id eq ${ticketId}` : `Id eq ${ticketId}`;
  return `?$filter=${selectedFilter}&$orderby=CreatedDate desc&$top=1&$count=true&${SIDE_VIEW_SELECTED_EXPAND}`;
};

/**
 * Filter the side-view tickets array after a ticket's data has been mutated,
 * removing tickets that no longer match the current dropdown filter.
 *
 * Index map (kept in sync with EditTicketsView dropdown):
 *  0 = All Active, 1 = My Work, 2 = Unassigned -> exclude Closed/Resolved
 *  3 = Closed -> include only Closed/Resolved
 *  4 = Deflected -> include only Deflected
 *
 * That map only holds while custom views are OFF. With them on, the same variable carries a
 * cpex_CustomView row Id instead - EditTicketsView's selectView sets it from customView.Id -
 * so a tenant whose view happens to have Id 3 or 4 gets read as Closed or Deflected and every
 * active row is dropped. Hence isCustomViewEnabled: see the guard below.
 */
export const filterSideViewTicketsForUpdatedTicket = <T extends { Id: any; StatusId: any }>(
  tickets: T[],
  updatedTicketId: number | string,
  updatedTicketData: Partial<T>,
  ticketStatuses: any[],
  activeDropdownIndex: number,
  isCustomViewEnabled = false
): T[] => {
  const closedStatusId = getStatusIdByGuidHelper(ticketStatuses, STATUS_GUID.CLOSED);
  const resolvedStatusId = getStatusIdByGuidHelper(ticketStatuses, STATUS_GUID.RESOLVED);
  const deflectedStatusId = getStatusIdByGuidHelper(ticketStatuses, STATUS_GUID.DEFLECTED);

  const merged = tickets.map(t => (t.Id == updatedTicketId ? { ...t, ...updatedTicketData } : t));

  // Bug 35324. With custom views on, activeDropdownIndex is a view Id, not an index into the
  // map above, so none of these branches mean what they say. There is nothing to fall back to
  // either: a custom view's membership comes from its own ViewFilters - priority, group, custom
  // properties - which the server evaluates and the client cannot recompute here. Patch the row
  // and leave the list to the next fetch, which is built from selectedCustomViewId.
  //
  // Defaults to false so the legacy dropdown keeps the behaviour the map describes.
  if (isCustomViewEnabled) return merged;

  // Without the status list we cannot tell which tickets belong in the current filter:
  // getStatusIdByGuidHelper returns 0 when it is empty, so every id above collapses to 0
  // and the comparisons drop rows they should keep. Patch the row, leave the list alone.
  // Reachable on a page refresh, where this runs before the statuses have loaded.
  if (!ticketStatuses || ticketStatuses.length === 0) return merged;

  if (activeDropdownIndex === 0 || activeDropdownIndex === 1 || activeDropdownIndex === 2) {
    return merged.filter(t => t.StatusId !== closedStatusId && t.StatusId !== resolvedStatusId);
  }
  if (activeDropdownIndex === 3) {
    return merged.filter(t => t.StatusId === closedStatusId || t.StatusId === resolvedStatusId);
  }
  if (activeDropdownIndex === 4) {
    return merged.filter(t => t.StatusId === deflectedStatusId);
  }
  return merged;
};
